import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { api } from '../api/client';
import { ApiError, Category, PendingImport } from '../api/types';
import { useBudget } from '../store/BudgetContext';
import { useToast } from '../components/Toast';
import { Icon, IconName, categoryIconName } from '../components/Icon';
import { theme } from '../theme';
import { InboxEvent, buildInboxEvents, markAllRead, readSet } from '../utils/inbox-events';
import { Tab } from '../components/Nav';

interface InboxProps {
  myUserId: string | null;
  onNavigate?: (tab: Tab) => void;
  onMarkedRead?: () => void;
}

export function Inbox({ myUserId, onNavigate, onMarkedRead }: InboxProps) {
  const { viewData, refresh } = useBudget();
  const toast = useToast();
  const [items, setItems] = useState<PendingImport[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.listPending();
      setItems(r.items);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Не удалось загрузить';
      toast.show(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  // Derived events
  const events = useMemo(
    () => (viewData ? buildInboxEvents(viewData, myUserId) : []),
    [viewData, myUserId],
  );

  // Инициальный снапшот прочитанных — для отображения маркера непрочитанного
  // (снапшот до markAllRead → показываем точку в первый рендер, затем сразу гасим)
  const [readKeys] = useState(() => readSet());

  // При первом рендере с событиями — помечаем все прочитанными
  // markedRef.current гардит от повторного вызова при пересчёте events/onMarkedRead
  const markedRef = useRef(false);
  useEffect(() => {
    if (events.length === 0 || markedRef.current) return;
    markedRef.current = true;
    markAllRead(events.map((e) => e.key));
    onMarkedRead?.();
  }, [events, onMarkedRead]);

  const cats = viewData?.categories ?? [];

  // частые категории расходов (60 дней) — кандидаты в чипы-предложения
  const frequent = useMemo(() => {
    const expense = cats
      .filter((c) => c.type === 'Расход')
      .sort((a, b) => a.sort_order - b.sort_order);
    const cut = new Date();
    cut.setDate(cut.getDate() - 60);
    const cutIso = `${cut.getFullYear()}-${String(cut.getMonth() + 1).padStart(2, '0')}-${String(cut.getDate()).padStart(2, '0')}`;
    const count = new Map<string, number>();
    for (const op of viewData?.operations ?? []) {
      if (op.type !== 'Расход' || op.date < cutIso) continue;
      count.set(op.category, (count.get(op.category) || 0) + 1);
    }
    return expense
      .map((c) => c.name)
      .sort((a, b) => (count.get(b) || 0) - (count.get(a) || 0));
  }, [viewData, cats]);

  return (
    <div style={{ padding: '26px 20px 100px' }}>
      <div
        style={{
          fontSize: 12,
          letterSpacing: 2,
          textTransform: 'uppercase',
          color: theme.colors.sub,
        }}
      >
        Импорты из Т-Банка
      </div>
      <h1 style={{ margin: '4px 0 10px', fontSize: 27, fontWeight: 400 }}>Входящие</h1>

      {/* Секция «События» — только если есть хотя бы одно */}
      {events.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div
            style={{
              fontSize: 12,
              letterSpacing: 2,
              textTransform: 'uppercase',
              color: theme.colors.sub,
              marginBottom: 8,
            }}
          >
            События
          </div>
          {events.map((ev) => (
            <EventRow
              key={ev.key}
              event={ev}
              unread={!readKeys.has(ev.key)}
              onTap={() => onNavigate?.(ev.tab)}
            />
          ))}
        </div>
      )}

      {loading && (
        <div style={{ color: theme.colors.sub, fontSize: 13 }}>Загружаю…</div>
      )}

      {!loading && items.length === 0 && (
        <div className="rise" style={{ padding: '40px 0', textAlign: 'center' }}>
          <div
            style={{
              fontFamily: theme.fontDisplay,
              fontWeight: 420,
              fontVariationSettings: "'opsz' 144, 'WONK' 1",
              fontSize: 20,
            }}
          >
            Разобрано
          </div>
          <div style={{ fontSize: 13, color: theme.colors.sub, fontStyle: 'italic', marginTop: 6 }}>
            новые операции из банка появятся здесь
          </div>
        </div>
      )}

      <div>
        {items.map((it) => (
          <PendingCard
            key={it.id}
            item={it}
            categories={cats}
            frequent={frequent}
            onResolved={async () => {
              await load();
              await refresh();
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Icon mapping for event kinds
// ---------------------------------------------------------------------------

const KIND_ICON: Record<InboxEvent['kind'], IconName> = {
  chest: 'chest',
  finale: 'star',
  milestone: 'map',
  partner_day: 'list',
};

// ---------------------------------------------------------------------------
// EventRow — строка события в стиле OpRow из History
// ---------------------------------------------------------------------------

function EventRow({
  event,
  unread,
  onTap,
}: {
  event: InboxEvent;
  unread: boolean;
  onTap: () => void;
}) {
  return (
    <div
      onClick={onTap}
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 12,
        padding: '12px 0',
        borderBottom: `1px solid ${theme.colors.line}`,
        cursor: 'pointer',
        position: 'relative',
      }}
    >
      {/* Маркер непрочитанного — чернильная точка слева */}
      {unread && (
        <span
          style={{
            position: 'absolute',
            left: -12,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 5,
            height: 5,
            borderRadius: '50%',
            background: theme.colors.ink,
            flexShrink: 0,
          }}
        />
      )}
      <Icon
        name={KIND_ICON[event.kind]}
        size={15}
        color={theme.colors.sub}
        style={{ position: 'relative', top: 2 }}
      />
      <span
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 15,
          color: theme.colors.ink,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {event.title}
        {event.sub && (
          <span style={{ fontSize: 12, color: theme.colors.sub, fontStyle: 'italic' }}>
            {' '}· {event.sub}
          </span>
        )}
      </span>
    </div>
  );
}

const SRC_LABEL: Record<string, string> = {
  ios_push: 'т-банк',
  tbank: 'т-банк',
};

function sourceCaption(it: PendingImport): string {
  const src = SRC_LABEL[it.source] ?? it.source;
  return `${src} · ${formatWhen(it.created_at)}`;
}

function formatWhen(raw: string): string {
  const d = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return raw.slice(0, 10);
  const key = (x: Date) => `${x.getFullYear()}-${x.getMonth()}-${x.getDate()}`;
  const now = new Date();
  const hm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  if (key(d) === key(now)) return `сегодня ${hm}`;
  const yest = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (key(d) === key(yest)) return `вчера ${hm}`;
  return `${d.getDate()}.${String(d.getMonth() + 1).padStart(2, '0')} ${hm}`;
}

/** Разбор в один жест: чипы категорий + правило-штамп + Принять/Удалить. */
function PendingCard({
  item,
  categories,
  frequent,
  onResolved,
}: {
  item: PendingImport;
  categories: Category[];
  frequent: string[];
  onResolved: () => Promise<void>;
}) {
  const toast = useToast();
  const [category, setCategory] = useState(item.suggested_category ?? '');
  const [amountStr, setAmountStr] = useState(item.amount != null ? String(item.amount) : '');
  const [rememberRule, setRememberRule] = useState(true);
  const [allCats, setAllCats] = useState(false);
  const [busy, setBusy] = useState(false);

  const expenseCats = useMemo(
    () => categories.filter((c) => c.type === 'Расход').sort((a, b) => a.sort_order - b.sort_order),
    [categories],
  );
  const byName = useMemo(
    () => new Map(expenseCats.map((c) => [c.name, c])),
    [expenseCats],
  );

  // чипы: предложенная категория + частые; выбранная всегда видна
  const chipNames = useMemo(() => {
    const names: string[] = [];
    const add = (n: string | null | undefined) => {
      if (n && !names.includes(n) && byName.has(n)) names.push(n);
    };
    add(item.suggested_category);
    for (const n of frequent) {
      if (names.length >= 4) break;
      add(n);
    }
    if (category && byName.has(category) && !names.includes(category)) {
      return [...names.slice(0, 3), category];
    }
    return names;
  }, [item.suggested_category, frequent, category, byName]);

  const amountN = Number(amountStr.replace(',', '.'));
  const canAccept = !!category && Number.isFinite(amountN) && amountN > 0 && !busy;
  const withRule = rememberRule && !!item.merchant;

  async function accept() {
    if (!canAccept) return;
    setBusy(true);
    try {
      await api.acceptPending(item.id, {
        category,
        amount: amountN,
        remember_rule: withRule,
      });
      const cat = byName.get(category);
      toast.reward({
        icon: cat ? categoryIconName(cat) : undefined,
        title: `Записано · ${category}`,
        amount: `−${Math.round(amountN).toLocaleString('ru-RU')} ₽`,
        coin: false,
        sub: withRule && item.merchant
          ? `правило: ${item.merchant.toLowerCase()} → ${category.toLowerCase()}`
          : undefined,
      });
      await onResolved();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Ошибка';
      toast.show(msg, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function dismiss() {
    setBusy(true);
    try {
      await api.dismissPending(item.id);
      toast.show('Удалено');
      await onResolved();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Ошибка';
      toast.show(msg, 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ padding: '16px 0', borderBottom: `1px solid ${theme.colors.line}` }}>
      {/* шапка: источник · дата / мерчант ← → сумма Fraunces 34 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="caption" style={{ fontSize: 11 }}>{sourceCaption(item)}</div>
          <div
            style={{
              fontSize: 16,
              marginTop: 3,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {item.merchant ?? item.raw_text}
          </div>
        </div>
        {item.amount != null ? (
          <span
            style={{
              fontFamily: theme.fontDisplay,
              fontVariationSettings: "'opsz' 144, 'WONK' 0",
              fontVariantNumeric: 'tabular-nums',
              fontSize: 34,
              lineHeight: 1,
              whiteSpace: 'nowrap',
            }}
          >
            {Math.round(item.amount).toLocaleString('ru-RU')}{' '}
            <span style={{ fontSize: 15, color: theme.colors.sub }}>₽</span>
          </span>
        ) : (
          // сумма не распарсилась — единственное место, где остаётся ввод
          <input
            type="number"
            inputMode="decimal"
            placeholder="0"
            value={amountStr}
            onChange={(e) => setAmountStr(e.target.value)}
            style={amountInput}
          />
        )}
      </div>

      {/* круглые чипы предлагаемых категорий + «ещё…» */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12 }}>
        {chipNames.map((name) => {
          const c = byName.get(name);
          const on = category === name;
          return (
            <button key={name} onClick={() => setCategory(name)} style={chip(on)}>
              {c && (
                <Icon
                  name={categoryIconName(c)}
                  size={14}
                  color={on ? theme.colors.paper : theme.colors.ink}
                />
              )}
              {name}
            </button>
          );
        })}
        <button onClick={() => setAllCats((v) => !v)} style={chipMore}>ещё…</button>
      </div>

      {/* полная сетка категорий (паттерн AddSheet) */}
      {allCats && (
        <div
          className="rise"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 6,
            marginTop: 10,
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          {expenseCats.map((c) => {
            const on = category === c.name;
            return (
              <button
                key={c.name}
                onClick={() => { setCategory(c.name); setAllCats(false); }}
                style={gridCell(on)}
              >
                <Icon name={categoryIconName(c)} size={18} color={on ? theme.colors.paper : theme.colors.ink} />
                {c.name}
              </button>
            );
          })}
        </div>
      )}

      {/* правило-штамп вместо чекбокса */}
      {item.merchant && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 12 }}>
          <button
            onClick={() => setRememberRule((v) => !v)}
            style={rememberRule ? stampOn : stampOff}
          >
            {rememberRule
              ? `правило: ${item.merchant.toLowerCase()} → ${(category || '?').toLowerCase()}`
              : 'запомнить правило'}
          </button>
          <span style={{ fontSize: 11.5, color: theme.colors.sub, fontStyle: 'italic', whiteSpace: 'nowrap' }}>
            тап — переключить
          </span>
        </div>
      )}

      {/* Primary «Принять» + Ghost «Удалить» */}
      <div style={{ display: 'flex', gap: 8, marginTop: 13 }}>
        <button
          onClick={accept}
          disabled={!canAccept}
          style={{
            flex: 1,
            padding: '12px 0',
            background: canAccept ? theme.colors.ink : theme.colors.line,
            color: canAccept ? theme.colors.paper : theme.colors.sub,
            border: 'none',
            borderRadius: theme.radius.sm,
            fontFamily: theme.fontDisplay,
            fontSize: 15,
            cursor: canAccept ? 'pointer' : 'not-allowed',
          }}
        >
          {busy ? '...' : 'Принять'}
        </button>
        <button
          onClick={dismiss}
          disabled={busy}
          style={{
            padding: '12px 16px',
            background: 'transparent',
            color: theme.colors.sub,
            border: `1px solid ${theme.colors.line}`,
            borderRadius: theme.radius.sm,
            fontFamily: theme.font,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          Удалить
        </button>
      </div>
    </div>
  );
}

/** Круглый чип категории (закон радиусов «Б»: палец трогает круглое). */
function chip(on: boolean): CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 13px',
    background: on ? theme.colors.ink : 'transparent',
    color: on ? theme.colors.paper : theme.colors.ink,
    border: `1px solid ${on ? theme.colors.ink : theme.colors.line}`,
    borderRadius: 999,
    fontFamily: theme.font,
    fontSize: 13,
    cursor: 'pointer',
  };
}

const chipMore: CSSProperties = {
  padding: '8px 13px',
  background: 'transparent',
  border: `1px dashed ${theme.colors.subSoft}`,
  borderRadius: 999,
  color: theme.colors.sub,
  fontFamily: theme.font,
  fontSize: 13,
  cursor: 'pointer',
};

/** Ячейка полной сетки категорий (паттерн AddSheet). */
function gridCell(on: boolean): CSSProperties {
  return {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    padding: '10px 2px',
    fontSize: 10,
    lineHeight: 1.1,
    textAlign: 'center',
    background: on ? theme.colors.ink : 'transparent',
    color: on ? theme.colors.paper : theme.colors.ink,
    border: `1px solid ${on ? theme.colors.ink : theme.colors.line}`,
    borderRadius: theme.radius.sm,
    fontFamily: theme.font,
    cursor: 'pointer',
  };
}

/** Активный штамп правила: мох, поворот −3°, острые углы. */
const stampOn: CSSProperties = {
  background: 'transparent',
  border: `1.5px solid ${theme.colors.green}`,
  color: theme.colors.green,
  transform: 'rotate(-3deg)',
  fontSize: 10,
  letterSpacing: '.14em',
  textTransform: 'uppercase',
  padding: '3px 8px',
  borderRadius: 0,
  fontFamily: theme.font,
  cursor: 'pointer',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

/** Неактивный: dashed sub, без поворота. */
const stampOff: CSSProperties = {
  ...stampOn,
  border: `1px dashed ${theme.colors.subSoft}`,
  color: theme.colors.sub,
  transform: 'none',
};

const amountInput: CSSProperties = {
  width: 110,
  padding: '6px 8px',
  border: `1px solid ${theme.colors.line}`,
  borderRadius: theme.radius.sm,
  fontFamily: theme.fontDisplay,
  fontVariantNumeric: 'tabular-nums',
  fontSize: 22,
  textAlign: 'right',
  background: 'transparent',
  color: theme.colors.ink,
  outline: 'none',
  boxSizing: 'border-box',
};
